import { useEffect, useRef, useState } from 'react';
import type { Editor } from 'grapesjs';
import { useEditorStore } from '../stores/editor-store';

const editorConfig = {
  fromElement: false,
  height: '100%',
  width: 'auto',
  storageManager: false,
  selectorManager: {
    // Preserve Tailwind class syntax including arbitrary values [brackets], colons, slashes
    escapeName: (name: string) =>
      `${name}`.trim().replace(/([^a-z0-9\w\-:/\[\]#%.]+)/gi, '-'),
  },
  panels: { defaults: [] },
  blockManager: {
    blocks: [],
  },
  deviceManager: {
    devices: [
      { name: 'Desktop', width: '' },
      { name: 'Tablet', width: '768px', widthMedia: '992px' },
      { name: 'Mobile', width: '375px', widthMedia: '480px' },
    ],
  },
  styleManager: {
    sectors: [
      {
        name: 'Layout',
        open: true,
        properties: [
          {
            type: 'composite',
            name: 'Margin',
            property: 'margin',
            properties: [
              { name: 'Top', property: 'margin-top', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Right', property: 'margin-right', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Bottom', property: 'margin-bottom', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Left', property: 'margin-left', type: 'integer', units: ['px', 'rem', '%'] },
            ],
          },
          {
            type: 'composite',
            name: 'Padding',
            property: 'padding',
            properties: [
              { name: 'Top', property: 'padding-top', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Right', property: 'padding-right', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Bottom', property: 'padding-bottom', type: 'integer', units: ['px', 'rem', '%'] },
              { name: 'Left', property: 'padding-left', type: 'integer', units: ['px', 'rem', '%'] },
            ],
          },
        ],
      },
      {
        name: 'Dimensions',
        open: false,
        properties: [
          { property: 'width', type: 'integer', units: ['px', '%', 'auto'] },
          { property: 'height', type: 'integer', units: ['px', '%', 'auto'] },
          { property: 'max-width', type: 'integer', units: ['px', '%'] },
          { property: 'min-height', type: 'integer', units: ['px', '%'] },
        ],
      },
      {
        name: 'Typography',
        open: false,
        properties: [
          { property: 'font-family' },
          { property: 'font-size', type: 'integer', units: ['px', 'rem', 'em'] },
          {
            property: 'font-weight', type: 'select', options: [
              { id: 'fw-300', value: '300', name: 'Light' },
              { id: 'fw-400', value: '400', name: 'Normal' },
              { id: 'fw-500', value: '500', name: 'Medium' },
              { id: 'fw-600', value: '600', name: 'Semi-Bold' },
              { id: 'fw-700', value: '700', name: 'Bold' },
            ]
          },
          { property: 'color', type: 'color' },
          {
            property: 'text-align', type: 'radio', options: [
              { id: 'ta-left', value: 'left', name: 'Left' },
              { id: 'ta-center', value: 'center', name: 'Center' },
              { id: 'ta-right', value: 'right', name: 'Right' },
            ]
          },
        ],
      },
      {
        name: 'Decorations',
        open: false,
        properties: [
          { property: 'background-color', type: 'color' },
          { property: 'border-radius', type: 'integer', units: ['px', 'rem'] },
          {
            property: 'border', type: 'composite', properties: [
              { property: 'border-width', type: 'integer', units: ['px'] },
              { property: 'border-style', type: 'select' },
              { property: 'border-color', type: 'color' },
            ]
          },
        ],
      },
    ],
  },
  canvas: {
    styles: [],
    scripts: ['/tailwind.js'],
  },
};

export function useGrapesEditor() {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const setEditorInstance = useEditorStore((s) => s.setEditorInstance);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    let cancelled = false;

    // Dynamically import GrapesJS so it doesn't block the initial bundle
    import('grapesjs').then((grapesjs) => {
      if (cancelled || !containerRef.current) return;

      const editor = grapesjs.default.init({
        container: containerRef.current,
        ...editorConfig,
      });

      // Define AI Edit command
      editor.Commands.add('tlb-ai-edit', {
        run(editor) {
          const selected = editor.getSelected();
          if (!selected) return;

          const el = selected.getEl();
          if (!el) return;

          const iframe = editor.Canvas.getFrameEl();
          const iframeRect = iframe.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();

          // Calculate absolute position on screen
          const panelWidth = 320; // w-80 is 20rem = 320px
          let x = iframeRect.left + elRect.right - panelWidth;

          // Ensure we don't go off-screen to the left if element is narrow
          // But keep it close to right side where toolbar is
          if (x < iframeRect.left) {
            x = iframeRect.left + elRect.left; // Fallback to left align if narrow
          }

          // Position just below the top edge (where toolbar usually is)
          // Toolbar is ~30px height, add some buffer
          let y = iframeRect.top + elRect.top + 45;

          useEditorStore.getState().setFloatingPanel({
            isOpen: true,
            x,
            y
          });
        }
      });

      // Add button to toolbar on selection
      editor.on('component:selected', (component) => {
        const toolbar = component.get('toolbar');
        if (!toolbar) return;

        // Check if button already exists to avoid duplicates
        const hasButton = toolbar.some((btn: any) => btn.command === 'tlb-ai-edit');
        if (!hasButton) {
          // Add Magic Wand button
          // Using an SVG similar to the 'Sparkles' icon from Lucide
          const aiBtn = {
            command: 'tlb-ai-edit',
            label: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sparkles"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>`,
            attributes: { title: 'Edit with AI' }
          };
          component.set('toolbar', [...toolbar, aiBtn]);
        }
      });

      // Close panel on deselect
      editor.on('component:deselected', () => {
        useEditorStore.getState().setFloatingPanel({ isOpen: false, x: 0, y: 0 });
      });

      editorRef.current = editor;
      setEditorInstance(editor);
      setLoading(false);

      // Add keyboard controls for moving elements
      editor.on('load', () => {
        const doc = editor.Canvas.getDocument();
        if (!doc) return;

        doc.addEventListener('keydown', (e) => {
          // Ignore if user is editing text inside a component
          const selected = editor.getSelected();
          if (!selected || editor.getEditing()) return;

          const step = e.shiftKey ? 10 : 1;
          const style = selected.getStyle();
          // Check computed style to see if we should use margin or position
          // But getStyle() returns element inline styles / class styles from GrapesJS model
          const position = String(style.position || 'static');

          let handled = false;

          if (['absolute', 'fixed', 'relative'].includes(position)) {
            const currentLeft = parseFloat(String(style.left || '0')) || 0;
            const currentTop = parseFloat(String(style.top || '0')) || 0;

            if (e.key === 'ArrowLeft') {
              selected.addStyle({ left: `${currentLeft - step}px` });
              handled = true;
            } else if (e.key === 'ArrowRight') {
              selected.addStyle({ left: `${currentLeft + step}px` });
              handled = true;
            } else if (e.key === 'ArrowUp') {
              selected.addStyle({ top: `${currentTop - step}px` });
              handled = true;
            } else if (e.key === 'ArrowDown') {
              selected.addStyle({ top: `${currentTop + step}px` });
              handled = true;
            }
          } else {
            // For static elements, use margins
            const currentMl = parseFloat(String(style['margin-left'] || '0')) || 0;
            const currentMt = parseFloat(String(style['margin-top'] || '0')) || 0;

            if (e.key === 'ArrowLeft') {
              selected.addStyle({ 'margin-left': `${currentMl - step}px` });
              handled = true;
            } else if (e.key === 'ArrowRight') {
              selected.addStyle({ 'margin-left': `${currentMl + step}px` });
              handled = true;
            } else if (e.key === 'ArrowUp') {
              selected.addStyle({ 'margin-top': `${currentMt - step}px` });
              handled = true;
            } else if (e.key === 'ArrowDown') {
              selected.addStyle({ 'margin-top': `${currentMt + step}px` });
              handled = true;
            }
          }

          if (handled) {
            e.preventDefault();
            // Trigger update
            editor.trigger('component:styleUpdate', selected);
          }
        });
      });
    });

    return () => {
      cancelled = true;
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
        setEditorInstance(null);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { containerRef, loading };
}
